import { useState } from "react";
import { Payload, Platform, PayloadCategory } from "@/types/profile";
import { PayloadCard } from "./PayloadCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, X } from "lucide-react";
import { 
  payloads,
  getAllCategories,
  getPayloadsByCategory,
  getPayloadsByPlatform 
} from "@/utils/payloads";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PayloadGridProps {
  selectedPlatforms: Platform[];
  selectedPayloads: string[];
  onSelectPayload: (payloadId: string) => void;
  onPayloadSelect?: (payload: Payload) => void;
}

export const PayloadGrid = ({
  selectedPlatforms,
  selectedPayloads,
  onSelectPayload,
  onPayloadSelect,
}: PayloadGridProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState<Platform[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<PayloadCategory | null>(null);

  const getPlatformLabel = (platform: Platform): string => {
    const platformLabels: Record<Platform, string> = {
      macos: "macOS",
      ios: "iOS",
      ipados: "iPadOS", 
      tvos: "tvOS"
    };
    return platformLabels[platform];
  };

  const togglePlatformFilter = (platform: Platform) => {
    setPlatformFilter(prev => 
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const clearFilters = () => {
    setPlatformFilter([]);
    setCategoryFilter(null);
    setSearchQuery("");
  };

  const filteredPayloads = payloads.filter(payload => {
    // Search filter
    if (searchQuery && !payload.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !payload.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Platform filter
    if (platformFilter.length > 0) {
      if (!platformFilter.some(platform => payload.supportedPlatforms.includes(platform))) {
        return false;
      }
    }

    // Category filter
    if (categoryFilter && payload.category !== categoryFilter) {
      return false;
    }

    return true;
  });

  const isPayloadSupported = (payload: Payload): boolean => {
    return selectedPlatforms.some(platform => 
      payload.supportedPlatforms.includes(platform)
    );
  };

  const handlePayloadClick = (payloadId: string) => {
    const payload = payloads.find(p => p.id === payloadId);
    if (payload) {
      onPayloadSelect?.(payload);
    }
    onSelectPayload(payloadId);
  };

  const categories = getAllCategories();
  const hasActiveFilters = platformFilter.length > 0 || categoryFilter || searchQuery;

  return (
    <div className="h-full flex flex-col">
      {/* Header with search and filters */}
      <div className="p-4 border-b space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search payloads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Platform filter */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <span className="text-sm font-medium">Platform Filter</span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-6 px-2 text-xs"
              >
                Clear all <X className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedPlatforms.map(platform => (
              <Button
                key={platform}
                variant={platformFilter.includes(platform) ? "default" : "outline"}
                size="sm"
                onClick={() => togglePlatformFilter(platform)}
                className="h-7 px-3 text-xs"
              >
                {getPlatformLabel(platform)}
              </Button>
            ))}
          </div>
        </div>

        {/* Category filter */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Category</span>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <Button
                key={category}
                variant={categoryFilter === category ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoryFilter(categoryFilter === category ? null : category)}
                className="h-7 px-3 text-xs"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <div className="text-sm text-muted-foreground">
          Showing {filteredPayloads.length} of {payloads.length} payloads
        </div>
      </div>

      {/* Payload grid */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {filteredPayloads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Filter className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No payloads match your filters</p>
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="mt-2"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredPayloads.map(payload => (
                <PayloadCard
                  key={payload.id}
                  payload={payload}
                  selectedPlatforms={selectedPlatforms}
                  isSelected={selectedPayloads.includes(payload.id)}
                  isSupported={isPayloadSupported(payload)}
                  onSelect={handlePayloadClick}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};